<script lang="ts">
export default {
  name: 'AppMenu',
};

interface MenuItemGroup {
  items: MenuItem[];
  hasDivider: boolean;
}

interface MenuItem {
  label: string;
  handler: () => void;
  hidden: () => boolean;
  hoverColor?: string;
}
</script>

<script setup lang="ts">
import { Menu } from '@lucide/vue';
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { AppState } from '@/store';
import { CanViewNotifications, IsUserAClient } from '@/utilities/AuthUtils';

const store = useStore<AppState>();
const router = useRouter();

const emit = defineEmits<{
  handleOption: [option: string];
}>();

// this needed to allow the button to toggle the menu
const isMenuOpen = ref(false);

// ---------
// computed
// ---------
const userName = computed(() => {
  return store.state.user?.name ?? '';
});

const userEmail = computed(() => {
  return store.state.user?.email ?? '';
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

const canViewNotifications = computed(() => {
  return CanViewNotifications(store.state.user);
});

const menuItemGroups = ref<MenuItemGroup[]>([
  {
    items: [
      { label: 'Platform Directory', handler: handleDirectory, hidden: () => isUserAClient.value },
      { label: 'Client Views', handler: handleClientView, hidden: () => isUserAClient.value },
      { label: 'Notifications', handler: handleNotificationsPage, hidden: () => !canViewNotifications.value },
    ],
    hasDivider: true,
  },
  {
    items: [
      { label: 'Help Center', handler: handleHelp, hidden: () => isUserAClient.value },
      { label: 'Submit Feedback', handler: handleFeedback, hidden: () => false },
      { label: 'Demo Environment', handler: handleDemoEnvironment, hidden: () => isUserAClient.value },
    ],
    hasDivider: true,
  },
  {
    items: [{ label: 'Logout', handler: handleLogout, hoverColor: 'hover:bg-red-600', hidden: () => false }],
    hasDivider: false,
  },
]);

// ---------
// watchers
// ---------
watch(isMenuOpen, (isOpen) => {
  if (!isOpen) {
    blurActiveElement();
  }
});

// ----------
// functions
// ----------
function blurActiveElement() {
  const activeElement = document.activeElement as HTMLElement;
  activeElement.blur();
}

function handleDirectory() {
  blurActiveElement();
  router.push('/directory');
}

function handleHelp() {
  blurActiveElement();
  window.open('https://cobbs-allen.sharefile.com/f/fodf52cc-2817-4922-bf95-8473a458c56a', '_blank');
}

function handleFeedback() {
  blurActiveElement();
  emit('handleOption', 'feedback');
}

function handleDemoEnvironment() {
  blurActiveElement();
  window.open('https://demo.platform.cacspecialty.com/', '_blank');
}

function handleClientView() {
  blurActiveElement();
  emit('handleOption', 'client-views');
}

function handleNotificationsPage() {
  blurActiveElement();
  emit('handleOption', 'notifications');
}

function handleLogout() {
  router.push('/logout');
}

function toggleMenuClick() {
  isMenuOpen.value = !isMenuOpen.value;
}

function isMenuGroupEmpty(group: MenuItemGroup): boolean {
  return group.items.every((item) => item?.hidden?.());
}
</script>

<template>
  <div class="dropdown dropdown-end dropdown-bottom mx-1 h-full">
    <div
      @click="toggleMenuClick"
      @blur="isMenuOpen = false"
      tabindex="0"
      role="button"
      class="flex h-full cursor-pointer items-center rounded px-3 py-2 hover:bg-white/10 transition-colors">
      <Menu :size="18" class="stroke-white" />
    </div>
    <div
      tabindex="0"
      class="dropdown-content card-compact card min-w-44 rounded-lg bg-dark pb-2 text-gray-200"
      @blur="isMenuOpen = false">
      <div class="card-body pt-3 pb-0">
        <div class="m-0 card-title justify-between gap-2">
          <div>
            <div class="text-sm">{{ userName }}</div>
            <div class="text-xs font-medium">{{ userEmail }}</div>
          </div>
        </div>
      </div>
      <div class="divider m-0 divider-accent px-2 opacity-20"></div>

      <div v-for="(group, groupIndex) in menuItemGroups" :key="groupIndex">
        <template v-if="!isMenuGroupEmpty(group)">
          <ul class="menu w-full gap-1 py-0">
            <li v-for="(item, itemIndex) in group.items" :key="itemIndex">
              <a
                v-if="!item.hidden()"
                @click="item.handler"
                class="text-base-300"
                :class="item.hoverColor ? item.hoverColor : 'hover:bg-gray-700'">
                {{ item.label }}
              </a>
            </li>
          </ul>
          <div v-if="group.hasDivider" class="divider m-0 divider-primary px-2 opacity-20"></div>
        </template>
      </div>
    </div>
  </div>
</template>
